/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/init-declarations */
import type { AnnuallyRecurringSchedule, DailyRecurringSchedule, MonthlyRecurringSchedule, WeeklyRecurringSchedule, RecurringScheduleResource } from "@octopusdeploy/octopus-server-client";
import { DayOfWeek, RecurringScheduleEndType, RecurringScheduleType, MonthlyScheduleType } from "@octopusdeploy/octopus-server-client";
import _ from "lodash";
import type { Moment } from "moment";
import React from "react";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { CardFill } from "~/components/form/Sections/ExpandableFormSection";
import { ExpandableFormSection, Select, Summary } from "~/components/form/index";
import DateFormatter from "~/utils/DateFormatter/index";
import DayOfWeekOrdinalHelper from "~/utils/DeploymentFreezeScheduleHelper/DayOfWeekOrdinalHelper";
import DaysDescriptionHelper from "~/utils/ScheduledTriggerDescriptionHelper/DaysDescriptionHelper";
import { ScheduleFormatter } from "~/utils/ScheduledTriggerDescriptionHelper/ScheduleFormatter";
import { DeploymentFreezeScheduleDialog } from "./DeploymentFreezeScheduleDialog";
import { FreezeDateTimePicker } from "./FreezeDateTimePicker";
import { RecurringFreezePreview } from "./RecurringFreezePreview";
enum DeploymentFreezeRecurrenceOptions {
    None = "none",
    Daily = "daily",
    Weekly = "weekly",
    Weekdays = "weekdays",
    Weekends = "weekends",
    DateOfMonth = "dateOfMonth",
    DayOfMonth = "dayOfMonth",
    Annually = "annually",
    Custom = "custom",
    Configured = ""
}
interface FreezeScheduleSectionProps {
    startDate: Moment;
    endDate: Moment;
    recurringSchedule?: RecurringScheduleResource;
    userTimezone: string;
    userUtcOffset: number;
    onScheduleUpdate: (startDate: Moment, endDate: Moment) => void;
    onRecurringScheduleUpdate: (schedule: RecurringScheduleResource | undefined) => void;
}
export const FreezeScheduleSection: React.FC<FreezeScheduleSectionProps> = ({ startDate, endDate, recurringSchedule, userTimezone, userUtcOffset, onScheduleUpdate, onRecurringScheduleUpdate }) => {
    const [showCustomScheduleDialog, setShowCustomScheduleDialog] = React.useState(false);
    const onDeploymentFreezeRecurranceUpdate = (recurrence: string | undefined) => {
        if (recurrence === DeploymentFreezeRecurrenceOptions.Custom) {
            setShowCustomScheduleDialog(true);
            return;
        }
        let newRecurringSchedule: RecurringScheduleResource | undefined;
        switch (recurrence) {
            case DeploymentFreezeRecurrenceOptions.None:
                newRecurringSchedule = undefined;
                break;
            case DeploymentFreezeRecurrenceOptions.Daily:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Daily,
                    Unit: 1,
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as DailyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.Weekly:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Weekly,
                    Unit: 1,
                    DaysOfWeek: [startDate.format("dddd") as DayOfWeek],
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as WeeklyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.Weekdays:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Weekly,
                    Unit: 1,
                    DaysOfWeek: [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday],
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as WeeklyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.Weekends:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Weekly,
                    Unit: 1,
                    DaysOfWeek: [DayOfWeek.Saturday, DayOfWeek.Sunday],
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as WeeklyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.DateOfMonth:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Monthly,
                    MonthlyScheduleType: MonthlyScheduleType.DateOfMonth,
                    Unit: 1,
                    DateOfMonth: startDate.date().toString(),
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as MonthlyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.DayOfMonth:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Monthly,
                    MonthlyScheduleType: MonthlyScheduleType.DayOfMonth,
                    Unit: 1,
                    DayNumberOfMonth: new DayOfWeekOrdinalHelper().getDayOfWeekOrdinalDescriptor(startDate),
                    DayOfWeek: startDate.format("dddd") as DayOfWeek,
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as MonthlyRecurringSchedule;
                break;
            case DeploymentFreezeRecurrenceOptions.Annually:
                newRecurringSchedule = {
                    Type: RecurringScheduleType.Annually,
                    Unit: 1,
                    UserUtcOffsetInMinutes: userUtcOffset,
                } as AnnuallyRecurringSchedule;
                break;
            default:
                newRecurringSchedule = undefined;
                break;
        }
        onRecurringScheduleUpdate(newRecurringSchedule);
    };
    const onCustomScheduleDialogClosed = () => {
        setShowCustomScheduleDialog(false);
    };
    const onRecurringScheduleChange = (schedule: RecurringScheduleResource | WeeklyRecurringSchedule | MonthlyRecurringSchedule) => {
        onRecurringScheduleUpdate(schedule);
        onCustomScheduleDialogClosed();
    };
    const isCustomSchedule = (schedule?: RecurringScheduleResource) => {
        if (!schedule)
            return false;
        if (schedule.Unit !== 1)
            return true;
        if (schedule.EndType && schedule.EndType !== RecurringScheduleEndType.Never)
            return true;
        if (schedule.Type === RecurringScheduleType.Weekly) {
            const weeklySchedule = schedule as WeeklyRecurringSchedule;
            return !(_.isEqual(weeklySchedule.DaysOfWeek, [startDate.format("dddd") as DayOfWeek]) ||
                _.isEqual(weeklySchedule.DaysOfWeek, [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday]) ||
                _.isEqual(weeklySchedule.DaysOfWeek, [DayOfWeek.Saturday, DayOfWeek.Sunday]));
        }
        if (schedule.Type === RecurringScheduleType.Monthly) {
            const monthlySchedule = schedule as MonthlyRecurringSchedule;
            switch (monthlySchedule.MonthlyScheduleType) {
                case MonthlyScheduleType.DateOfMonth:
                    return monthlySchedule.DateOfMonth !== startDate.date().toString();
                case MonthlyScheduleType.DayOfMonth:
                    return monthlySchedule.DayNumberOfMonth !== new DayOfWeekOrdinalHelper().getDayOfWeekOrdinalDescriptor(startDate) && monthlySchedule.DayOfWeek !== (startDate.format("dddd") as DayOfWeek);
            }
            return false;
        }
        return false;
    };
    const getRecurringScheduleOption = (schedule?: RecurringScheduleResource) => {
        if (!schedule)
            return DeploymentFreezeRecurrenceOptions.None;
        if (isCustomSchedule(schedule)) {
            return DeploymentFreezeRecurrenceOptions.Configured;
        }
        if (schedule?.Type === RecurringScheduleType.Daily) {
            return DeploymentFreezeRecurrenceOptions.Daily;
        }
        if (schedule?.Type === RecurringScheduleType.Weekly) {
            const daysPerWeekRecurringSchedule = schedule as WeeklyRecurringSchedule;
            if (new DaysDescriptionHelper(daysPerWeekRecurringSchedule).runsOnWeekdays()) {
                return DeploymentFreezeRecurrenceOptions.Weekdays;
            }
            if (new DaysDescriptionHelper(daysPerWeekRecurringSchedule).runsOnWeekends()) {
                return DeploymentFreezeRecurrenceOptions.Weekends;
            }
            return DeploymentFreezeRecurrenceOptions.Weekly;
        }
        if (schedule?.Type === RecurringScheduleType.Monthly) {
            const daysPerMonthRecurringSchedule = schedule as MonthlyRecurringSchedule;
            if (daysPerMonthRecurringSchedule.MonthlyScheduleType === MonthlyScheduleType.DateOfMonth) {
                return DeploymentFreezeRecurrenceOptions.DateOfMonth;
            }
            else {
                return DeploymentFreezeRecurrenceOptions.DayOfMonth;
            }
        }
        if (schedule?.Type === RecurringScheduleType.Annually) {
            return DeploymentFreezeRecurrenceOptions.Annually;
        }
        return DeploymentFreezeRecurrenceOptions.Custom;
    };
    const getRecurringScheduleOptions = () => {
        const recurringScheduleOptions = [
            { value: DeploymentFreezeRecurrenceOptions.None, text: "Does not repeat" },
            { value: DeploymentFreezeRecurrenceOptions.Daily, text: "Repeats daily" },
            { value: DeploymentFreezeRecurrenceOptions.Weekly, text: `Repeats weekly on ${startDate.format("dddd")}` },
            { value: DeploymentFreezeRecurrenceOptions.Weekdays, text: "Repeats on all weekdays" },
            { value: DeploymentFreezeRecurrenceOptions.Weekends, text: "Repeats on all weekends" },
            { value: DeploymentFreezeRecurrenceOptions.DateOfMonth, text: `Repeats monthly on the ${startDate.format("Do")}` },
            { value: DeploymentFreezeRecurrenceOptions.DayOfMonth, text: `Repeats monthly on the ${new DayOfWeekOrdinalHelper().getDayOfWeekOrdinalDescription(startDate)} ${startDate.format("dddd")}` },
            { value: DeploymentFreezeRecurrenceOptions.Annually, text: `Repeats annually on ${startDate.format("MMMM D")}` },
        ];
        if (isCustomSchedule(recurringSchedule)) {
            recurringScheduleOptions.push({ value: DeploymentFreezeRecurrenceOptions.Configured, text: ScheduleFormatter.buildScheduleSummary(recurringSchedule, startDate) });
        }
        recurringScheduleOptions.push({ value: DeploymentFreezeRecurrenceOptions.Custom, text: "Custom..." });
        return recurringScheduleOptions;
    };
    const buildPickerSummary = () => Summary.summary(`This deployment freeze is scheduled to start on ${DateFormatter.dateToLongFormat(startDate)}`);
    const helpText = {
        schedule: {
            help: "Specify the schedule for this deployment freeze.",
        },
    };
    return (<ExpandableFormSection key="schedule" errorKey="schedule" title="Schedule" summary={buildPickerSummary()} help={helpText.schedule.help} containerKey="FreezeEdit" isExpandedByDefault={true} fillCardWidth={CardFill.FillRight}>
            <FreezeDateTimePicker initialStartDate={startDate} initialEndDate={endDate} updateScheduledDatesOnModel={onScheduleUpdate} onStartDateChange={() => onDeploymentFreezeRecurranceUpdate(DeploymentFreezeRecurrenceOptions.None)}/>
            {isFeatureToggleEnabled("RecurringDeploymentFreezesFeatureToggle") && (<>
                    <h4>Recurrence</h4>
                    <div>
                        <Select value={getRecurringScheduleOption(recurringSchedule)} allowClear={true} onChange={onDeploymentFreezeRecurranceUpdate} items={getRecurringScheduleOptions()} sortItems={false}/>
                        <DeploymentFreezeScheduleDialog userUtcOffset={userUtcOffset} show={showCustomScheduleDialog} startDate={startDate} schedule={recurringSchedule} onClosed={onCustomScheduleDialogClosed} onScheduleChange={onRecurringScheduleChange}/>
                    </div>
                </>)}
            {recurringSchedule && <RecurringFreezePreview recurringSchedule={recurringSchedule} startDate={startDate} endDate={endDate} userTimezone={userTimezone}/>}
        </ExpandableFormSection>);
};
FreezeScheduleSection.displayName = "FreezeScheduleSection"
